import React, { useId } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon';
import InfoTooltip from '../Icon/InfoTooltip/Index';
import { describeField } from '../foundations/fieldDescription';
import {
    FORM_INPUT_LABEL_PLACEMENT,
    FORM_INPUT_LABEL_PLACEMENTS,
    FORM_INPUT_SIZE,
    FORM_INPUT_SIZES,
} from './constants';

// Design System — FormInput
// The soft text field: 1px @secondary-100 border, @radius-lg, xs shadow,
// @secondary-300 border on focus. Two label placements (outside/inside the
// box), three sizes, leading icon, prefix/suffix add-ons (currency, protocol,
// country select, copy button…), single-line or multiline (textarea).
// Controlled like a native input: `onChange` receives the DOM event.
// Styles: ./index.less ships via less/ats_style.less (white-label pipeline) —
// no JSX .less import, per frontend-structure.mdc.

const FormInput = ({
    label,
    labelPlacement = FORM_INPUT_LABEL_PLACEMENT.OUTSIDE,
    size = FORM_INPUT_SIZE.LG,
    type = 'text',
    value,
    defaultValue,
    onChange,
    placeholder,
    name,
    id,
    required = false,
    disabled = false,
    error = false,
    helperText,
    tooltip,
    leadingIcon,
    prefix,
    suffix,
    multiline = false,
    rows = 3,
    className,
    onFocus,
    onBlur,
    ref,
    ...rest
}) => {
    const autoId = useId();
    const inputId = id || autoId;
    const safeSize = FORM_INPUT_SIZES.has(size) ? size : FORM_INPUT_SIZE.LG;
    const placement = FORM_INPUT_LABEL_PLACEMENTS.has(labelPlacement)
        ? labelPlacement
        : FORM_INPUT_LABEL_PLACEMENT.OUTSIDE;
    const labelInside = placement === FORM_INPUT_LABEL_PLACEMENT.INSIDE;
    const errorText = typeof error === 'boolean' ? null : error;
    const { errorId, helperId, describedBy } = describeField(inputId, { errorText, helperText });

    const labelContent = label != null && label !== '' && (
        <>
            {label}
            {required && <span className="ds-form-input__required" aria-hidden="true"> *</span>}
            {tooltip && <InfoTooltip value={tooltip} />}
        </>
    );

    const controlProps = {
        id: inputId,
        ref,
        name,
        value,
        defaultValue,
        onChange,
        onFocus,
        onBlur,
        placeholder,
        disabled,
        required,
        className: 'ds-form-input__control',
        'aria-invalid': Boolean(error) || undefined,
        'aria-describedby': describedBy,
        ...rest,
    };

    return (
        <div
            className={classNames(
                'ds-form-input',
                `ds-form-input--${safeSize}`,
                `ds-form-input--label-${placement}`,
                {
                    'ds-form-input--error': Boolean(error),
                    'ds-form-input--disabled': disabled,
                    'ds-form-input--multiline': multiline,
                },
                className,
            )}
        >
            {!labelInside && labelContent && (
                <label className="ds-form-input__label" htmlFor={inputId}>
                    {labelContent}
                </label>
            )}
            <div className="ds-form-input__field">
                {labelInside && labelContent && (
                    <label className="ds-form-input__inner-label" htmlFor={inputId}>
                        {labelContent}
                    </label>
                )}
                <div className="ds-form-input__row">
                    {leadingIcon && (
                        <Icon value={leadingIcon} noMargin className="ds-form-input__lead-icon" />
                    )}
                    {prefix != null && <span className="ds-form-input__affix">{prefix}</span>}
                    {multiline ? (
                        <textarea rows={rows} {...controlProps} />
                    ) : (
                        <input type={type} {...controlProps} />
                    )}
                    {suffix != null && (
                        <span className="ds-form-input__affix ds-form-input__affix--suffix">
                            {suffix}
                        </span>
                    )}
                </div>
            </div>
            {errorText && (
                <div className="ds-form-input__error-text" id={errorId}>
                    {errorText}
                </div>
            )}
            {helperId && (
                <div className="ds-form-input__helper" id={helperId}>
                    {helperText}
                </div>
            )}
        </div>
    );
};

FormInput.propTypes = {
    /** Field label; placement decides whether it renders above or inside the box */
    label: PropTypes.node,
    /** 'outside' (above the box, default) or 'inside' (softbox — small label on the first line) */
    labelPlacement: PropTypes.oneOf([...FORM_INPUT_LABEL_PLACEMENTS]),
    /** Control size from the design scale; lg (default) is the design baseline */
    size: PropTypes.oneOf([...FORM_INPUT_SIZES]),
    /** Native input type (single-line only) */
    type: PropTypes.string,
    /** Controlled value */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Uncontrolled initial value */
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Native change handler — receives the DOM event; required when `value` is set */
    onChange: PropTypes.func,
    /** Placeholder text */
    placeholder: PropTypes.string,
    /** Form field name */
    name: PropTypes.string,
    /** Input id (auto-generated when omitted; label always targets it) */
    id: PropTypes.string,
    /** Marks the field required and shows the asterisk in the label */
    required: PropTypes.bool,
    /** Disabled state — tinted box, muted text */
    disabled: PropTypes.bool,
    /** true for the error border only, or a node to also render the error message */
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
    /** Hint below the field (hidden while an error message shows) */
    helperText: PropTypes.node,
    /** InfoTooltip rendered next to the label */
    tooltip: PropTypes.node,
    /** core/Icon name rendered at the start of the box (e.g. 'mail-01', 'search-md') */
    leadingIcon: PropTypes.string,
    /** Add-on before the input, e.g. '$', 'http://', or a country <select> */
    prefix: PropTypes.node,
    /** Add-on after the input, e.g. a currency <select> or a copy button */
    suffix: PropTypes.node,
    /** Render a textarea instead of a single-line input */
    multiline: PropTypes.bool,
    /** Textarea rows (multiline only) */
    rows: PropTypes.number,
    /** Extra class on the root element */
    className: PropTypes.string,
    /** Native focus handler */
    onFocus: PropTypes.func,
    /** Native blur handler */
    onBlur: PropTypes.func,
    /** Ref to the underlying input/textarea element */
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

export { FORM_INPUT_SIZE, FORM_INPUT_SIZES, FORM_INPUT_LABEL_PLACEMENT, FORM_INPUT_LABEL_PLACEMENTS };
export default FormInput;
